import type { ListQuery } from "client/api/common/ListQuery";
import axiosClient from "../axios-client";
import type { CloudSubscriptionDto, SubscriptionGroupDto } from "./CloudSubscriptionApi";
import type { EntityDto } from "./EntityDto";
import type { ListEntitiesResponse } from "./ListEntitiesResponse";
import type { ServerLicenseDto } from "./ServerLicenseApi";
import type { UserGrantDto } from "./UserGrantApi";

const BASE_URL = "users";

export interface UserDto extends EntityDto {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  octopusIdId?: string;
}

export type GetUsersResponse = ListEntitiesResponse<UserDto>;

export const getUsers = async (query?: ListQuery): Promise<GetUsersResponse> => {
  const response = await axiosClient.get<GetUsersResponse>(BASE_URL, {
    params: query,
  });
  return response.data;
};

export const getUserById = async (userId: string): Promise<UserDto> => {
  const response = await axiosClient.get<UserDto>(`${BASE_URL}/${userId}`);
  return response.data;
};

export const getUserExistsById = async (userId: string): Promise<boolean> => {
  const response = await axiosClient.head(`${BASE_URL}/${userId}`, { validateStatus: undefined });
  return response.status === 200;
};

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName?: string;
}
export const createUser = async (request: CreateUserRequest): Promise<UserDto> => {
  const response = await axiosClient.post<UserDto>(BASE_URL, request);
  return response.data;
};

export interface UpdateUserRequest {
  firstName: string;
  lastName?: string;
  email?: string;
}
export const updateUser = async (userId: string, request: UpdateUserRequest): Promise<UserDto> => {
  const response = await axiosClient.put<UserDto>(`${BASE_URL}/${userId}`, request);
  return response.data;
};

export const deleteUser = async (userId: string): Promise<boolean> => {
  const response = await axiosClient.delete(`${BASE_URL}/${userId}`);
  return response.status == 204;
};

export interface UserRelatedServerLicenseDto {
  userGrant: UserGrantDto;
  serverLicense: ServerLicenseDto;
}
export interface ListUserRelatedServerLicensesDto {
  directServerLicenses: Array<UserRelatedServerLicenseDto>;
  indirectServerLicenses: Array<UserRelatedServerLicenseDto>;
}
export const getUserRelatedServerLicenses = async (userId: string): Promise<ListUserRelatedServerLicensesDto> => {
  const response = await axiosClient.get<ListUserRelatedServerLicensesDto>(`${BASE_URL}/${userId}/server-licenses`);
  return response.data;
};

export interface UserRelatedCloudSubscriptionDto {
  userGrant: UserGrantDto;
  cloudSubscription: CloudSubscriptionDto;
}
export interface ListUserRelatedCloudSubscriptionsDto {
  directCloudSubscriptions: Array<UserRelatedCloudSubscriptionDto>;
  indirectCloudSubscriptions: Array<UserRelatedCloudSubscriptionDto>;
}
export const getUserRelatedCloudSubscriptions = async (
  userId: string
): Promise<ListUserRelatedCloudSubscriptionsDto> => {
  const response = await axiosClient.get<ListUserRelatedCloudSubscriptionsDto>(
    `${BASE_URL}/${userId}/cloud-subscriptions`
  );
  return response.data;
};

export interface UserRelatedSubscriptionGroupDto {
  userGrant: UserGrantDto;
  subscriptionGroup: SubscriptionGroupDto;
}
export interface ListUserRelatedSubscriptionGroupsDto {
  subscriptionGroups: Array<UserRelatedSubscriptionGroupDto>;
}
export const getUserRelatedSubscriptionGroups = async (
  userId: string
): Promise<ListUserRelatedSubscriptionGroupsDto> => {
  const response = await axiosClient.get<ListUserRelatedSubscriptionGroupsDto>(
    `${BASE_URL}/${userId}/subscription-groups`
  );
  return response.data;
};
